import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { z } from "zod";

/**
 * Champs de déclaration d'âge (date de naissance + attestation).
 *
 * Le formulaire n'écrit jamais directement en base : la RPC serveur
 * `complete_age_onboarding` calcule l'âge et applique le seuil configuré.
 */
export const ageDeclarationSchema = z.object({
  dateOfBirth: z
    .string()
    .min(1, { message: "La date de naissance est obligatoire" })
    .refine((value) => !Number.isNaN(Date.parse(value)), {
      message: "La date de naissance renseignée est invalide",
    }),
  attested: z.literal(true, {
    errorMap: () => ({ message: "Vous devez cocher l'attestation pour continuer" }),
  }),
});

interface AgeOnboardingFormProps {
  /** Appelé après une déclaration acceptée par le serveur. */
  onSuccess: () => void;
  /** Message d'erreur affiché à l'utilisateur (refus ou validation). */
  onError: (message: string) => void;
  submitLabel?: string;
}

export const AgeOnboardingForm = ({
  onSuccess,
  onError,
  submitLabel = "Valider et accéder à Tama",
}: AgeOnboardingFormProps) => {
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const parsed = ageDeclarationSchema.safeParse({ dateOfBirth, attested });
      if (!parsed.success) {
        onError(parsed.error.errors[0].message);
        return;
      }

      const { data, error } = await supabase.rpc("complete_age_onboarding" as any, {
        p_date_of_birth: dateOfBirth,
        p_attested: true,
      });

      if (error) {
        onError("La validation a échoué. Merci de réessayer.");
        return;
      }

      const result = (data ?? {}) as { ok?: boolean; error?: string };
      if (!result.ok) {
        onError(result.error || "La validation a échoué. Merci de réessayer.");
        return;
      }

      onSuccess();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="dateOfBirth">
          Date de naissance <span className="text-destructive">*</span>
        </Label>
        <div className="ios-birth-date-field h-10 min-w-0 max-w-full overflow-hidden rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          <Input
            id="dateOfBirth"
            type="date"
            className="ios-birth-date-input h-full min-w-0 max-w-full rounded-[inherit] border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            value={dateOfBirth}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(event) => setDateOfBirth(event.target.value)}
            required
            disabled={submitting}
          />
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="ageAttested"
          checked={attested}
          onCheckedChange={(checked) => setAttested(checked === true)}
          disabled={submitting}
        />
        <Label htmlFor="ageAttested" className="text-sm font-normal leading-snug">
          J'atteste être majeur(e) et certifie que les informations d'identité fournies sont
          exactes. <span className="text-destructive">*</span>
        </Label>
      </div>

      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Validation...
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
};

export default AgeOnboardingForm;
